import React, { useEffect, useMemo, useState } from "react";
import SimpleSwapInterface from "./SimpleSwapInterface";
import "./EnhancedSwapPage.css";

interface MemeTokenConfig {
  id: string;
  name: string;
  symbol: string;
  description: string;
  image?: string;
  contractPackageId: string;
  bondingCurveId: string;
  metadataId?: string | null;
  coinType: string;
  poolCreated?: boolean;
  decimals: number;
  poolId?: string | null;
  swapPlatformFeeObj?: string | null;
}

interface EnhancedSwapPageProps {
  tokens: MemeTokenConfig[];
  user?: any;
  userSessionToken?: string | null;
  onNavigate: (path: string) => void;
}

const EnhancedSwapPage: React.FC<EnhancedSwapPageProps> = ({
  tokens,
  user,
  userSessionToken,
  onNavigate,
}) => {
  // Filter only graduated tokens (premium level)
  const graduatedTokens = useMemo(
    () => tokens.filter((token) => token.poolCreated === true),
    [tokens]
  );

  // Get tokenId from URL hash if present (e.g., #/swaponly/18)
  const getTokenIdFromUrl = () => {
    const hash = window.location.hash.replace("#", "");
    const parts = hash.split("/");
    return parts.length > 2 ? parts[2] : null;
  };

  const [selectedId, setSelectedId] = useState<string | null>(getTokenIdFromUrl);

  const selectedToken = useMemo(() => {
    const raw = graduatedTokens.find((token) => token.id === selectedId);
    if (!raw) return null;
    return {
      ...raw,
      decimals: Number.isFinite(Number(raw.decimals)) ? Number(raw.decimals) : 9,
      poolId: raw.poolId || null,
      swapPlatformFeeObj: raw.swapPlatformFeeObj || null,
    };
  }, [graduatedTokens, selectedId]);

  const handleTokenClick = (tokenId: string) => {
    setSelectedId(tokenId);
    window.location.hash = `/swaponly/${tokenId}`;
  };

  const handleBackToList = () => {
    setSelectedId(null);
    window.location.hash = "/swaponly";
  };

  // Show token list view if no token selected
  if (!selectedToken) {
    return (
      <div className="swap-page">
        <section className="swap-hero-card">
          <div className="swap-hero-copy">
            <div className="premium-badge-header">
              <span className="swap-tag premium">🌟 Premium Level Swap</span>
              <span className="fee-badge">0.5% Fee</span>
            </div>
            <h1 className="swap-hero-title">Swap Graduated Tokens</h1>
            <p className="swap-hero-subtitle">
              Trade tokens that have reached premium level with cross-chain liquidity through Cetus DEX pools. Only graduated tokens with active liquidity pools are available for swapping.
            </p>
            <div className="swap-hero-actions">
              <button
                type="button"
                className="swap-secondary-action"
                onClick={() => onNavigate("/discover")}
              >
                Discover Trending
              </button>
            </div>
          </div>
          <div className="swap-hero-stats">
            <div className="swap-stat-card premium">
              <span className="swap-stat-label">Premium Tokens</span>
              <span className="swap-stat-value">{graduatedTokens.length}</span>
              <span className="swap-stat-sublabel">Available for Swap</span>
            </div>
            <div className="swap-stat-card premium">
              <span className="swap-stat-label">Liquidity Status</span>
              <span className="swap-stat-badge live">
                ✓ Cross-Chain Live
              </span>
              <span className="swap-stat-sublabel">Cetus DEX Pool</span>
            </div>
          </div>
        </section>

        <div className="swap-token-grid">
          {graduatedTokens.map((token) => (
            <button
              type="button"
              key={token.id}
              className="swap-token-card"
              onClick={() => handleTokenClick(token.id)}
            >
              <div className="swap-token-card-header">
                <span className="swap-token-card-icon premium" aria-hidden="true">
                  {token.image ? "🪙" : "💎"}
                </span>
                <span className="premium-star-badge">⭐</span>
              </div>
              <div className="swap-token-card-body">
                <div className="swap-token-card-name">{token.name}</div>
                <div className="swap-token-card-symbol">${token.symbol}</div>
              </div>
              <div className="swap-token-card-footer">
                <span className="swap-token-card-status">
                  ✓ Premium Pool
                </span>
                <span className="swap-token-card-action">Swap →</span>
              </div>
            </button>
          ))}
          {!graduatedTokens.length && (
            <div className="swap-empty-state-full">
              <span className="swap-empty-icon" aria-hidden="true">
                🌟
              </span>
              <p className="empty-title">No Premium Tokens Yet</p>
              <p className="empty-subtitle">Tokens must reach the threshold and graduate to appear here.</p>
              <button
                type="button"
                className="empty-action"
                onClick={() => onNavigate("/discover")}
              >
                View All Tokens
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show swap interface when token is selected
  return (
    <div className="swap-page swap-page-interface">
      <div className="swap-interface-full">
        <div className="swap-interface-header">
          <button
            type="button"
            className="swap-back-button"
            onClick={handleBackToList}
          >
            ← Back to Tokens
          </button>
          <div className="swap-interface-title">
            <h2>{selectedToken.name} (${selectedToken.symbol})</h2>
            <span className="premium-badge-small">⭐ Premium</span>
          </div>
        </div>
        <SimpleSwapInterface
          token={selectedToken}
          user={user}
          userSessionToken={userSessionToken}
        />
      </div>
    </div>
  );
};

export default EnhancedSwapPage;
